import { useMutation } from '@apollo/client';
import React, { useContext } from 'react';
import { StockLocationRoleAssignmentContext } from '../../../../../../../context/StockLocationRoleAssignmentContext';
import {
  DeleteStockLocationRoleAssignmentResponse,
  DeleteStockLocationRoleAssignmentVariables,
  StockLocationRoleAssignmentMutation,
} from '../../../../../../../graphql/stockLocationRoleAssignment.graphql';
import { useToast } from '../../../../../../../providers/ToastProvider';
import {
  StockLocationRoleAssignment,
  StockLocationRoleAssignmentInput,
} from '../../../../../../../types/stock_location_role_assignment';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../../types/translation/Translator';
import { Button, ButtonVariant, CancelButton } from '../../../../../../components/Button';
import { AlertModal } from '../../../../../../components/Modal';
import { ToastType } from '../../../../../../components/Toast';

interface DeleteStockLocationRoleForStockLocationModalProps {
  assignment: StockLocationRoleAssignment;
  setOpen: (open: boolean) => void;
}

export default function DeleteStockLocationRoleForStockLocationModal({
  assignment,
  setOpen,
}: DeleteStockLocationRoleForStockLocationModalProps) {
  const { showToast } = useToast();

  const { stockLocationRoles, setStockLocationRoles } = useContext(StockLocationRoleAssignmentContext);

  const [remove, { loading }] = useMutation<
    DeleteStockLocationRoleAssignmentResponse,
    DeleteStockLocationRoleAssignmentVariables
  >(StockLocationRoleAssignmentMutation.remove, {
    variables: {
      stockLocationRoleAssignments: [new StockLocationRoleAssignmentInput(assignment).forDelete()],
    },
    onCompleted: response => {
      response.deleteStockLocationRoleAssignment.forEach(a => {
        const forLocation = stockLocationRoles.get(a.stockLocationId);
        if (!forLocation) return;
        stockLocationRoles.set(
          a.stockLocationId,
          forLocation.filter(la => la.id !== a.id),
        );
      });
      setStockLocationRoles(new Map(stockLocationRoles));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationRoleAssignmentRemovedSuccessfullyToastTitle).singular.upper,
      });
      setOpen(false);
    },
  });

  return (
    <AlertModal
      title={t(TranslationKey.deleteStockLocationRolesModalTitle).singular.upper}
      open={true}
      setOpen={setOpen}
    >
      <div className='grid gap-4'>
        <p className='subtitle'>{t(TranslationKey.deleteStockLocationRolesModalSubtitle).singular.upper}</p>
        <div className='flex justify-end gap-2'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <Button
            variant={ButtonVariant.danger}
            text={t(TranslationKey.delete).singular.upper}
            onClick={() => remove()}
            loading={loading}
          />
        </div>
      </div>
    </AlertModal>
  );
}
